import { useEffect, useState } from 'react';
import {
  Button,
  Flex,
  Loader,
  Modal,
  Stack,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconArrowLeft, IconSearch } from '@tabler/icons-react';
import { useGetPatient, useSearchPatients } from 'api/resource';
import { Healthcard, Payor, Patient, SearchPatientResult } from 'interfaces';
import { useCurrentSiteStore } from 'store/SiteStore';
import { PatientClaimHistoryColumns } from 'columns/PatientClaimHistory';
import { AllKeys } from '../ModalComponents';
import { ClaimPatientHistoryTable } from './ClaimPatientHistoryTable';
import { PatientPaymentHistoryTable } from './PatientPaymentHistoryTable';
import { formatPatientName } from '@billing/validation/patient';

interface PatientHistoryModalProps {
  payors: Payor[];
  open?: boolean;
  onClose?: () => void;
  onClaimHistoryTotalChange?: (total: number) => void;
  variant?: 'modal' | 'inline';
  initialPatientID?: string;
  initialPatient?: Patient | null;
  initialHealthcard?: Healthcard | null;
}

type SelectedPatient = Patient | SearchPatientResult;

const formatHealthcardNumbers = (
  patient: SelectedPatient,
  initialHealthcard?: Healthcard | null
) => {
  const healthcardNumbers = Array.from(
    new Set(
      [...(patient.healthcards ?? []), initialHealthcard]
        .map((healthcard) => healthcard?.number)
        .filter((number): number is string => Boolean(number))
    )
  );

  if (healthcardNumbers.length === 0) return null;

  return `${
    healthcardNumbers.length === 1 ? 'PHIN' : 'PHINs'
  }: ${healthcardNumbers.join(', ')}`;
};

const claimHistoryColumns: AllKeys[] = [
  'serviceStart',
  'debits.requestedAmount',
  'debits.paidAmount',
  'providerID',
  'siteID',
  'tariffs',
  'tariffs.icd9',
  'debits.eobs',
  'status',
  'claimNumber',
  'payorID',
];
export const PatientHistoryModal: React.FC<PatientHistoryModalProps> = ({
  payors,
  open = false,
  onClose,
  onClaimHistoryTotalChange,
  variant = 'modal',
  initialPatientID,
  initialPatient,
  initialHealthcard,
}) => {
  const isInline = variant === 'inline';
  const isOpen = isInline || open;
  const { organizationID } = useCurrentSiteStore();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue] = useDebouncedValue(searchValue, 500);
  const [selectedPatient, setSelectedPatient] =
    useState<SelectedPatient | null>(null);
  const columns = PatientClaimHistoryColumns();
  const { data: fetchedPatient } = useGetPatient({
    id: initialPatientID ?? '',
  });

  const {
    data: searchResults = [],
    isLoading,
    isFetching,
  } = useSearchPatients({
    searchValue: debouncedSearchValue,
    organizationID,
  });

  const reset = () => {
    setSearchValue('');
    setSelectedPatient(null);
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
      return;
    }

    setSearchValue('');
    setSelectedPatient(fetchedPatient ?? initialPatient);
  }, [isOpen, fetchedPatient, initialPatient, initialPatientID]);

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const renderSearch = () => (
    <Stack gap="md" p="lg">
      <TextInput
        autoFocus
        label="Search patient history"
        description="Search by PHIN, patient name, or date of birth"
        placeholder="Enter a PHIN, name, or DOB"
        leftSection={<IconSearch size={18} />}
        value={searchValue}
        onChange={(event) => setSearchValue(event.currentTarget.value)}
        data-testid="patient-history-search-input"
      />
      {(isLoading || isFetching) && (
        <Flex justify="center" py="lg">
          <Loader size="sm" />
        </Flex>
      )}
      {!isLoading && !isFetching && searchValue && debouncedSearchValue && (
        <Stack gap="xs" data-testid="patient-history-search-results">
          {searchResults.length === 0 ? (
            <Text c="dimmed" ta="center" py="lg">
              No patients found
            </Text>
          ) : (
            searchResults.map((patient) => {
              const phins = patient.healthcards
                ?.map((healthcard) => healthcard.number)
                .filter(Boolean)
                .join(', ');

              return (
                <Button
                  key={patient.id}
                  variant="default"
                  color="gray"
                  fullWidth
                  justify="flex-start"
                  h="auto"
                  py="sm"
                  onClick={() => setSelectedPatient(patient)}
                >
                  <Stack gap={2} align="flex-start">
                    <Flex gap={8} align="center">
                      <Text fw={600}>
                        {formatPatientName(patient.firstName, patient.lastName)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {`DOB: ${patient.dateOfBirth || 'N/A'}`}
                      </Text>
                    </Flex>
                    <Text size="sm" c="dimmed">
                      {`PHIN: ${phins || 'N/A'}`}
                    </Text>
                  </Stack>
                </Button>
              );
            })
          )}
        </Stack>
      )}
    </Stack>
  );

  const renderHistory = (patient: SelectedPatient) => (
    <Stack h="100%" gap="md">
      <Flex justify="space-between" align="center">
        <Stack gap={0}>
          <Text fw={600}>
            {formatPatientName(patient.firstName, patient.lastName)}
          </Text>
          <Text size="sm" c="dimmed">
            {`DOB: ${patient.dateOfBirth || 'N/A'}`}
          </Text>
          {formatHealthcardNumbers(patient, initialHealthcard) && (
            <Text size="sm" c="dimmed">
              {formatHealthcardNumbers(patient, initialHealthcard)}
            </Text>
          )}
        </Stack>
        {!initialPatientID && (
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={reset}
          >
            Search again
          </Button>
        )}
      </Flex>
      <Tabs defaultValue="claims" h="100%">
        <Tabs.List>
          <Tabs.Tab value="claims">Claim History</Tabs.Tab>
          <Tabs.Tab value="payments">Payment History</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="claims" pt="md">
          <ClaimPatientHistoryTable
            patientID={patient.id}
            columns={columns}
            visibleColumns={claimHistoryColumns}
            pageSize={10}
            onTotalChange={onClaimHistoryTotalChange}
          />
        </Tabs.Panel>
        <Tabs.Panel value="payments" pt="md">
          <PatientPaymentHistoryTable
            patientID={patient.id}
            patient={patient}
            healthcard={initialHealthcard ?? patient.healthcards?.[0]}
            payors={payors}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );

  const content = selectedPatient
    ? renderHistory(selectedPatient)
    : renderSearch();

  if (isInline) {
    return content;
  }

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      title="Patient History"
      size="90%"
      centered
      closeOnEscape
      closeOnClickOutside={false}
    >
      {content}
    </Modal>
  );
};
